import React from 'react';
import { Typography, Box, Divider } from '@mui/material';
import { useNavigateWithScrollMemory } from '../../../hooks/useNavigateWithScrollMemory';
import { Link as RouterLink } from 'react-router-dom';
import AlbumCover from '../../../components/common/AlbumCover';
import theme from '../../../theme';
import RatingReadOnly from '../../../components/common/RatingReadOnly';
import LikeCount from '../../../components/common/LikeCount';
import CommentCount from '../../../components/common/CommentCount';
import ArtistLink from '../../../components/common/ArtistLink';
import AlbumTitleLink from '../../../components/common/AlbumTitleLink';

const MyReviewCard = ({ data, referrerType }) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const albumGenres =
    data.album?.genres ||
    data.album?.genreNames ||
    data.album?.albumGenres ||
    data.genreNames ||
    data.albumGenres ||
    [];
  const albumUrl = data.album.slug
    ? `/reviews/${data.album.slug}`
    : `/reviews/${data.album.albumSource?.toLowerCase()}/${data.album.sourceId}`;
  const reviewUrl = `${albumUrl}/user-reviews/${data.review.id}`;

  const navState = referrerType ? { state: { referrerType } } : undefined;

  const handleNavigate = (e, url) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      navigateWithScrollMemory(url, navState);
    }
  };

  const metaRow = (
    <Box display="flex" gap={1} alignItems="center" sx={{ mt: { xs: 0.75, md: 2 } }}>
      <Typography
        fontSize={{ xs: 12, md: 13, lg: 14 }}
        sx={{ color: theme.palette.black[4], whiteSpace: 'nowrap' }}
      >
        {data.review.createdAt}
      </Typography>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          height: '8px',
          alignSelf: 'center',
          backgroundColor: theme.palette.grey.line,
        }}
      />
      <Box display="flex" gap={{ xs: 1, md: 1.5 }} alignItems="center">
        <LikeCount likeCount={data.review.likeCount} />
        <CommentCount commentCount={data.review.commentCount} />
      </Box>
    </Box>
  );

  const reviewContent = (
    <Typography
      component={RouterLink}
      to={reviewUrl}
      state={navState?.state}
      onClick={(e) => handleNavigate(e, reviewUrl)}
      fontSize={{ xs: 13, lg: 14 }}
      color={theme.palette.black[6]}
      sx={{
        textDecoration: 'none',
        display: '-webkit-box',
        WebkitLineClamp: { xs: 3, md: 3 },
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        lineHeight: '1.5',
      }}
    >
      {data.review.content}
    </Typography>
  );

  return (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' }, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
          }}
        >
          <Box
            component={RouterLink}
            to={albumUrl}
            state={navState?.state}
            onClick={(e) => handleNavigate(e, albumUrl)}
            sx={{ display: 'block', width: '34%', minWidth: 112, maxWidth: 136, flexShrink: 0 }}
          >
            <AlbumCover
              imageUrl={data.album.imageUrl}
              altText={data.album.title}
              isStarred={data.album.isStarred}
              sx={{
                aspectRatio: '1 / 1',
                width: '100%',
                cursor: 'pointer',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignSelf: 'stretch',
            }}
          >
            <AlbumTitleLink
              title={data.album.title}
              to={albumUrl}
              contentRating={data.album.contentRating}
              sx={{ fontSize: 14, mb: 0.75, lineHeight: 1.25 }}
              lineClamp={1}
              iconSx={{ width: '12px', height: '12px' }}
            />
            <ArtistLink
              artistName={data.artist?.name}
              artistSource={data.artist?.artistSource}
              artistSourceId={data.artist?.sourceId}
              artistSlug={data.artist?.slug}
              artistDbId={data.artist?.id}
              sx={{ fontSize: 13, mb: 0.75, lineHeight: 1.25, color: theme.palette.black[4] }}
            />
            {albumGenres.length > 0 && (
              <Typography
                fontSize={13}
                sx={{
                  mb: 0.75,
                  color: theme.palette.black[4],
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.25,
                }}
              >
                {albumGenres.slice(0, 3).join(', ')}
              </Typography>
            )}
            <RatingReadOnly value={data.review.rating} variant="responsive" />
            {metaRow}
          </Box>
        </Box>
        <Box sx={{ mt: 1.25 }}>{reviewContent}</Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: { xs: 1.25, md: 2, lg: 2.5 },
        }}
      >
        {/* 이미지 */}
        <Box
          component={RouterLink}
          to={albumUrl}
          state={navState?.state}
          onClick={(e) => handleNavigate(e, albumUrl)}
          sx={{
            display: 'block',
            width: { xs: '30%', md: '20%', lg: '25%' },
            minWidth: { xs: 96, md: 168 },
            maxWidth: { md: 220 },
            flexShrink: 0,
          }}
        >
          <AlbumCover
            imageUrl={data.album.imageUrl}
            altText={data.album.title}
            isStarred={data.album.isStarred}
            sx={{
              aspectRatio: '1 / 1',
              width: '100%',
              cursor: 'pointer',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </Box>

        {/* 정보 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'stretch',
            justifyContent: 'center',
            width: '100%',
            minWidth: 0,
          }}
        >
          <AlbumTitleLink
            title={data.album.title}
            to={albumUrl}
            contentRating={data.album.contentRating}
            sx={{ fontSize: { xs: 14, lg: 18 }, mb: { xs: 1, lg: 2 } }}
            iconSx={{
              width: { xs: '12px', md: '14px', lg: '16px' },
              height: { xs: '12px', md: '14px', lg: '16px' },
            }}
          />
          <ArtistLink
            artistName={data.artist?.name}
            artistSource={data.artist?.artistSource}
            artistSourceId={data.artist?.sourceId}
            artistSlug={data.artist?.slug}
            artistDbId={data.artist?.id}
            sx={{
              fontSize: { xs: 13, lg: 16 },
              mb: { xs: 1.5, lg: 2 },
              color: { xs: theme.palette.black[4], md: theme.palette.black[9] },
            }}
          />

          {albumGenres.length > 0 && (
            <Typography
              fontSize={{ xs: 13, lg: 14 }}
              sx={{
                mb: { xs: 1.5, lg: 2 },
                color: theme.palette.black[4],
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                lineHeight: 1.4,
              }}
              title={albumGenres.slice(0, 3).join(', ')}
            >
              {albumGenres.slice(0, 3).join(', ')}
            </Typography>
          )}

          <RatingReadOnly value={data.review.rating} variant="responsive" />

          <Box sx={{ mt: { xs: 1.5, lg: 2 } }}>{reviewContent}</Box>

          {/* 날짜 | 좋아요 수, 댓글 수 */}
          {metaRow}
        </Box>
      </Box>
    </>
  );
};

export default MyReviewCard;
